// @ts-nocheck
// Exact live version restored from the production sourcemap (portal.rebels.io, 2026-10-23).
import { jsx as _jsx, jsxs as _jsxs } from 'react/jsx-runtime';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { burstSuccessSpark } from '@ui/components/common/micro-interactions';
import { Alert, Box, Button, CircularProgress, Dialog, Grid, IconButton, Typography } from '@mui/material';
import { Add, CloseCircle, Minus } from 'iconsax-react';
import { WidgetHeader } from '../../../../components/common/header/widgetHeader/WidgetHeader';
import { QInput } from '../../../../components/common/inputs/QInput';
import CustomSelect from '@ui/components/common/inputs/CustomSelect';
import { useAppDispatch } from '@application/store/useStore';
import { bulkRegisterHoursAsync } from '@application/features/HoursRegistration/hoursRegistrationThunk';
import { getTimeSheetCountsByStatusAsync } from '@application/features/timeSheet/timeSheetThunk';
import { translate } from '@application/common/helpers/translate';
import { formatDisplayDate } from '../hours-dates';
import { hourRowsSnapshot } from '../hourRowsSnapshot';
import { toRegistrationContextWireType } from '@application/features/HoursRegistration/models/registration-context';
import HoursRegistrationApi from '@infrastructure/hours-registration-api';
import { getErrorPayload } from '@application/common/helpers/api-helper';
import { WeekHoursMatrix } from '../WeekHoursMatrix';
import { createHourEntryId, hourWriteRefreshMessage } from '@application/features/HoursRegistration/hour-write-safety';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const WORKDAY_SYSTEM_CODE = 'WORKDAY';
const NO_EXPORT_SYSTEM_CODE = 'NO_EXPORT';
const asApiError = (error) => {
    if (error && typeof error === 'object' && typeof error.status === 'number')
        return error;
    return getErrorPayload(error);
};
export const FillHours = ({ open, handleClose, week, context, onSaved, presentation = 'dialog', onEditorState }) => {
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState([]);
    const [hourTypesByDate, setHourTypesByDate] = useState({});
    const [errors, setErrors] = useState({});
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [requiresRefresh, setRequiresRefresh] = useState(false);
    const requestInFlight = useRef(false);
    const outcomeUncertain = useRef(false);
    const loadedWeekVersion = useRef();
    // Dirty is derived from the rows: every edit path counts, and reverting to the
    // loaded values (after hour-type defaults are applied) is clean again.
    const [baseline, setBaseline] = useState(() => hourRowsSnapshot([]));
    const dirty = useMemo(() => hourRowsSnapshot(rows) !== baseline, [rows, baseline]);
    useEffect(() => {
        onEditorState?.({ dirty, saving: isSaving, uncertain: requiresRefresh });
    }, [dirty, isSaving, requiresRefresh, onEditorState]);
    useEffect(() => {
        if (outcomeUncertain.current)
            return;
        if (!open || !week || !context) {
            setErrors({});
            setSaveError(null);
            return;
        }
        const initialRows = week.rows.map((row) => ({
            localId: `row-${row.id}`,
            persistedId: row.id,
            date: row.date,
            assignmentReasonId: row.assignmentReasonId ?? '',
            // Unfilled placeholder rows stay empty (saved as 0); persisted values, including 0, are shown as stored.
            hours: row.assignmentReasonId || (row.hours !== null && row.hours !== undefined && Number(row.hours) !== 0) ? String(row.hours ?? 0) : '',
            isPersisted: Boolean(row.assignmentReasonId),
            storedDisplayName: row.displayName ?? row.client,
            storedSystemCode: row.systemCode
        }));
        setRows(initialRows);
        setBaseline(hourRowsSnapshot(initialRows));
        setErrors({});
        loadedWeekVersion.current = week.weekVersion || undefined;
        const missingMetadata = !loadedWeekVersion.current || week.rows.some((row) => !row.id);
        setRequiresRefresh(missingMetadata);
        setSaveError(missingMetadata ? 'This page is missing the current week version. Reload the page before saving hours.' : null);
        let active = true;
        const loadHourTypes = async () => {
            setIsLoadingTypes(true);
            try {
                const uniqueDates = Array.from(new Set(week.rows.map((row) => row.date)));
                const entries = await Promise.all(uniqueDates.map(async (date) => {
                    const response = await HoursRegistrationApi.getHourTypesForContext({
                        contextType: context.contextType,
                        contextId: context.contextId,
                        date
                    });
                    return [date, response.data];
                }));
                if (!active)
                    return;
                const byDate = Object.fromEntries(entries);
                setHourTypesByDate(byDate);
                const withDefaults = (current) => current.map((row) => {
                    if (row.assignmentReasonId)
                        return row;
                    const available = byDate[row.date] ?? [];
                    const defaultType = context.contextType === 'ClientAssignment'
                        ? (available.find((type) => type.systemCode === WORKDAY_SYSTEM_CODE) ?? available[0])
                        : available[0];
                    return { ...row, assignmentReasonId: defaultType?.id ?? '' };
                });
                // Default hour types are not a user change: the baseline includes them.
                setBaseline(hourRowsSnapshot(withDefaults(initialRows)));
                setRows(withDefaults);
            }
            catch (error) {
                if (!active)
                    return;
                setHourTypesByDate({});
                const apiError = asApiError(error);
                const reference = apiError.correlationId ? ` Reference: ${apiError.correlationId}.` : '';
                setSaveError(`Available hour types could not be loaded. Your existing input remains unchanged.${reference}`);
            }
            finally {
                if (active)
                    setIsLoadingTypes(false);
            }
        };
        void loadHourTypes();
        return () => {
            active = false;
        };
    }, [context, open, week]);
    const rowsByDate = useMemo(() => {
        const grouped = new Map();
        rows.forEach((row) => grouped.set(row.date, [...(grouped.get(row.date) ?? []), row]));
        return Array.from(grouped.entries());
    }, [rows]);
    const typeForRow = (row) => (hourTypesByDate[row.date] ?? []).find((type) => type.id === row.assignmentReasonId);
    const countsTowardsDailyTotal = (row) => {
        const type = typeForRow(row);
        return type ? type.exportPolicy !== 'NeverExport' : row.storedSystemCode !== NO_EXPORT_SYSTEM_CODE;
    };
    const isRetainedTechnicalPlaceholder = (row) => row.isPersisted && row.storedSystemCode === NO_EXPORT_SYSTEM_CODE && !typeForRow(row) && Number(row.hours) === 0;
    const optionsForRow = (row) => {
        const types = hourTypesByDate[row.date] ?? [];
        const options = types.map((type) => ({ label: type.displayName, value: type.id }));
        if (row.assignmentReasonId && !types.some((type) => type.id === row.assignmentReasonId)) {
            options.push({
                label: `${row.storedDisplayName || 'Previously saved hour type'} · unavailable`,
                value: row.assignmentReasonId
            });
        }
        return options;
    };
    const handleReasonChange = (localId, value) => {
        setRows((current) => current.map((row) => (row.localId === localId ? { ...row, assignmentReasonId: value } : row)));
        setErrors((current) => ({ ...current, [localId]: { ...current[localId], assignmentReasonId: undefined } }));
    };
    const handleHoursChange = (localId, value) => {
        setRows((current) => current.map((row) => (row.localId === localId ? { ...row, hours: value } : row)));
        setErrors((current) => ({ ...current, [localId]: { ...current[localId], hours: undefined } }));
    };
    const addRow = (date) => {
        const available = hourTypesByDate[date] ?? [];
        const defaultType = context?.contextType === 'ClientAssignment'
            ? (available.find((type) => type.systemCode === WORKDAY_SYSTEM_CODE) ?? available[0])
            : available[0];
        const id = createHourEntryId();
        setRows((current) => [
            ...current,
            {
                localId: `new-${id}`,
                persistedId: id,
                date,
                assignmentReasonId: defaultType?.id ?? '',
                hours: '',
                isPersisted: false
            }
        ]);
    };
    const removeRow = (localId) => {
        setRows((current) => current.filter((row) => row.localId !== localId));
        setErrors((current) => {
            const next = { ...current };
            delete next[localId];
            return next;
        });
    };
    const validate = () => {
        const nextErrors = {};
        const totals = new Map();
        rows.forEach((row) => {
            const rowErrors = {};
            const available = hourTypesByDate[row.date] ?? [];
            if (!row.assignmentReasonId) {
                rowErrors.assignmentReasonId = translate('validations.fieldIsRequired');
            }
            else if (!available.some((type) => type.id === row.assignmentReasonId) && !isRetainedTechnicalPlaceholder(row)) {
                rowErrors.assignmentReasonId = 'Choose an hour type available for this date.';
            }
            // An empty field means 0 hours.
            const normalized = String(row.hours ?? '').trim();
            const numeric = normalized === '' ? 0 : Number(normalized);
            if (normalized.includes('.'))
                rowErrors.hours = translate('validations.invalidDecimalWholeNumber');
            else if (!Number.isInteger(numeric) || numeric < 0)
                rowErrors.hours = translate('validations.invalidWholeNumber');
            else if (numeric > 17)
                rowErrors.hours = translate('validations.maximumIntegerAllowed', { count: 18 });
            if (countsTowardsDailyTotal(row)) {
                totals.set(row.date, (totals.get(row.date) ?? 0) + (Number.isFinite(numeric) ? numeric : 0));
            }
            if (Object.values(rowErrors).some(Boolean))
                nextErrors[row.localId] = rowErrors;
        });
        rows.forEach((row) => {
            if ((totals.get(row.date) ?? 0) > 17) {
                nextErrors[row.localId] = {
                    ...nextErrors[row.localId],
                    hours: translate('validations.maximumHoursAllowedInADay', {
                        date: formatDisplayDate(row.date),
                        count: 17
                    })
                };
            }
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };
    const saveWeeklyHours = async () => {
        if (!week || !context || !dirty || requestInFlight.current || requiresRefresh || outcomeUncertain.current || rows.length === 0 || !validate())
            return;
        if (!loadedWeekVersion.current || rows.some((row) => !row.persistedId)) {
            outcomeUncertain.current = true;
            setRequiresRefresh(true);
            setSaveError('This page is missing safe-save information. Your input is still visible; reload before saving again.');
            return;
        }
        const contextType = toRegistrationContextWireType(context.contextType);
        const dataToSend = rows.map((row) => ({
            year: week.year,
            month: week.month,
            weekNo: week.weekNo,
            assignmentId: context.contextType === 'ClientAssignment' ? context.contextId : EMPTY_GUID,
            contextType,
            contextId: context.contextId,
            assignmentReasonId: row.assignmentReasonId,
            NoOfHours: String(row.hours ?? '').trim() === '' ? 0 : Number(row.hours),
            Id: row.persistedId,
            expectedWeekVersion: loadedWeekVersion.current,
            date: row.date
        }));
        requestInFlight.current = true;
        setIsSaving(true);
        setSaveError(null);
        try {
            await dispatch(bulkRegisterHoursAsync(dataToSend)).unwrap();
            // Decoration only, after the existing save has succeeded.
            burstSuccessSpark();
            setBaseline(hourRowsSnapshot(rows));
            await onSaved();
            await dispatch(getTimeSheetCountsByStatusAsync());
            handleClose();
        }
        catch (error) {
            const apiError = asApiError(error);
            const code = apiError.code ? ` (${apiError.code})` : '';
            const reference = apiError.correlationId ? ` Reference: ${apiError.correlationId}.` : '';
            const refreshMessage = hourWriteRefreshMessage(apiError);
            if (refreshMessage) {
                outcomeUncertain.current = true;
                setRequiresRefresh(true);
                setSaveError(`${refreshMessage}${code}${reference}`);
                return;
            }
            const affected = rows
                .slice(0, 3)
                .map((row) => `${formatDisplayDate(row.date)} · ${typeForRow(row)?.displayName || row.storedDisplayName || 'hour type'}`)
                .join('; ');
            const remaining = rows.length > 3 ? `; and ${rows.length - 3} more` : '';
            setSaveError(`Week ${week.weekNo} could not be saved${code}. Your input is still here. Check: ${affected}${remaining}.${reference}`);
        }
        finally {
            requestInFlight.current = false;
            setIsSaving(false);
        }
    };
    const totalHours = rows.reduce((total, row) => {
        const type = typeForRow(row);
        const neverExport = type?.exportPolicy === 'NeverExport' || row.storedSystemCode === NO_EXPORT_SYSTEM_CODE;
        return neverExport ? total : total + (Number(row.hours) || 0);
    }, 0);
    const routeSummary = context?.contextType === 'ClientAssignment'
        ? `Client-routed hour types go to ${context.displayName}. Other available hour types go automatically to ${context.internalHoursDepartmentName ? `Internal Department ${context.internalHoursDepartmentName}` : 'your linked Internal Department'}.`
        : `All available hour types go automatically to ${context?.displayName || 'your Internal Department'}. Workday is not available in this context.`;
    if (presentation === 'inline' && week && context) {
        const clientHours = rows.reduce((sum, row) => sum + (typeForRow(row)?.routingTarget === 'ClientAssignment' ? Number(row.hours) || 0 : 0), 0);
        return _jsxs(Box, {
            children: [
                saveError && _jsx(Alert, { severity: 'error', sx: { m: 2 }, children: saveError }),
                requiresRefresh &&
                    _jsxs(Box, {
                        sx: { px: 2, pb: 2 },
                        children: [
                            _jsx(Button, {
                                variant: 'outlined',
                                onClick: () => window.location.reload(),
                                children: 'Reload page to check saved hours'
                            }),
                            _jsx(Typography, {
                                variant: 'body2',
                                children: 'Reloading clears this draft. Review saved hours before entering anything again.'
                            })
                        ]
                    }),
                isLoadingTypes
                    ? _jsx(Box, {
                        sx: { minHeight: 280, display: 'grid', placeItems: 'center' },
                        children: _jsx(CircularProgress, { 'aria-label': 'Loading available hour types' })
                    })
                    : _jsxs(Box, {
                        sx: { display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 240px' } },
                        children: [
                            _jsx(WeekHoursMatrix, {
                                rows: rows,
                                types: hourTypesByDate,
                                errors: errors,
                                context: context,
                                disabled: isSaving || requiresRefresh,
                                onChange: (next) => {
                                    setRows(next);
                                    setErrors({});
                                }
                            }),
                            _jsxs(Box, {
                                sx: {
                                    p: 2,
                                    borderLeft: { lg: '1px solid' },
                                    borderTop: { xs: '1px solid', lg: 0 },
                                    borderColor: 'divider',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5
                                },
                                children: [
                                    _jsx(Typography, {
                                        role: 'status',
                                        fontSize: 12,
                                        color: dirty ? 'warning.dark' : 'text.secondary',
                                        children: isSaving
                                            ? 'Saving…'
                                            : requiresRefresh
                                                ? 'Check saved hours'
                                                : dirty
                                                    ? 'Unsaved changes'
                                                    : 'No unsaved changes'
                                    }),
                                    _jsxs(Box, {
                                        children: [
                                            _jsx(Typography, { fontWeight: 600, children: 'This week' }),
                                            _jsxs(Typography, {
                                                className: 'weekly-hours-total',
                                                fontSize: 40,
                                                fontWeight: 700,
                                                sx: { fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 },
                                                children: [totalHours, ' h']
                                            })
                                        ]
                                    }),
                                    context.contextType === 'ClientAssignment' &&
                                        _jsxs(Box, {
                                            sx: {
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto',
                                                gap: 1,
                                                pt: 2,
                                                borderTop: '1px solid',
                                                borderColor: 'divider'
                                            },
                                            children: [
                                                _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: 'Client hours' }),
                                                _jsxs(Typography, { variant: 'body2', fontWeight: 600, children: [clientHours, ' h'] }),
                                                _jsx(Typography, {
                                                    variant: 'body2',
                                                    color: 'text.secondary',
                                                    children: 'Internal hours'
                                                }),
                                                _jsxs(Typography, {
                                                    variant: 'body2',
                                                    fontWeight: 600,
                                                    children: [totalHours - clientHours, ' h']
                                                })
                                            ]
                                        }),
                                    _jsx(Typography, {
                                        variant: 'caption',
                                        color: 'text.secondary',
                                        sx: { lineHeight: 1.5 },
                                        children: routeSummary
                                    }),
                                    _jsx(Button, {
                                        'data-action-motion': 'true',
                                        fullWidth: true,
                                        variant: 'contained',
                                        size: 'large',
                                        onClick: saveWeeklyHours,
                                        sx: { mt: 'auto' },
                                        disabled: !dirty ||
                                            isSaving ||
                                            requiresRefresh ||
                                            rows.length === 0 ||
                                            Object.keys(hourTypesByDate).length === 0 ||
                                            Object.values(hourTypesByDate).some((types) => types.length === 0),
                                        children: isSaving ? 'Saving weekly hours…' : 'Save weekly hours'
                                    })
                                ]
                            })
                        ]
                    })
            ]
        });
    }
    return _jsxs(Dialog, {
        open: open,
        onClose: isSaving || requiresRefresh ? undefined : handleClose,
        maxWidth: false,
        BackdropProps: { sx: { backdropFilter: 'blur(3px)', backgroundColor: 'rgba(74, 74, 74, 0.25)' } },
        PaperProps: { sx: { width: { xs: 'calc(100vw - 24px)', md: 760 }, maxWidth: '100%', maxHeight: '90vh' } },
        children: [
            _jsxs(WidgetHeader, {
                background: 'white',
                children: [
                    _jsxs(Box, {
                        minWidth: 0,
                        children: [
                            _jsx(Typography, {
                                fontSize: '20px',
                                color: 'black',
                                noWrap: true,
                                children: context?.displayName || 'Hour registration'
                            }),
                            _jsxs(Typography, { variant: 'caption', color: 'text.secondary', children: ['Week ', week?.weekNo] })
                        ]
                    }),
                    _jsx(IconButton, {
                        onClick: handleClose,
                        disabled: isSaving || requiresRefresh,
                        'aria-label': 'Close weekly hours',
                        children: _jsx(CloseCircle, { color: 'black' })
                    })
                ]
            }),
            _jsxs(Box, {
                sx: { px: { xs: 2, md: 3 }, py: 2.5, overflowY: 'auto' },
                children: [
                    _jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 2 }, children: routeSummary }),
                    saveError && _jsx(Alert, { severity: 'error', sx: { mb: 2 }, children: saveError }),
                    requiresRefresh &&
                        _jsxs(Box, {
                            sx: { mb: 2 },
                            children: [
                                _jsx(Button, {
                                    onClick: () => window.location.reload(),
                                    variant: 'outlined',
                                    children: 'Reload page to check saved hours'
                                }),
                                _jsx(Typography, {
                                    variant: 'body2',
                                    sx: { mt: 1 },
                                    children: 'Reloading clears this draft. Review the saved hours for this week before entering anything again.'
                                })
                            ]
                        }),
                    isLoadingTypes
                        ? _jsx(Box, {
                            sx: { minHeight: 220, display: 'grid', placeItems: 'center' },
                            children: _jsx(CircularProgress, { size: 28 })
                        })
                        : _jsxs(Grid, {
                            container: true,
                            spacing: 2,
                            children: [
                                _jsx(Grid, {
                                    item: true,
                                    xs: 4,
                                    md: 3.25,
                                    children: _jsx(Typography, { variant: 'subtitle2', children: 'Date' })
                                }),
                                _jsx(Grid, {
                                    item: true,
                                    xs: 5,
                                    md: 4.75,
                                    children: _jsx(Typography, { variant: 'subtitle2', children: 'Registration' })
                                }),
                                _jsx(Grid, {
                                    item: true,
                                    xs: 3,
                                    md: 3,
                                    children: _jsx(Typography, { variant: 'subtitle2', children: 'Register' })
                                }),
                                rowsByDate.map(([date, dateRows]) => dateRows.map((row, index) => {
                                    const options = optionsForRow(row);
                                    const selectedType = typeForRow(row);
                                    const internalDepartmentName = (selectedType?.routingTarget === 'InternalDepartment' &&
                                        context?.internalHoursDepartmentName) ||
                                        context?.internalDepartmentCode;
                                    const routeHint = selectedType?.routingTarget === 'ClientAssignment'
                                        ? `Client · ${context?.displayName}`
                                        : selectedType
                                            ? `Internal Department${internalDepartmentName ? ` · ${internalDepartmentName}` : ''}`
                                            : '';
                                    const totalForDate = dateRows.reduce((sum, item) => sum + (countsTowardsDailyTotal(item) ? Number(item.hours) || 0 : 0), 0);
                                    return _jsxs(React.Fragment, {
                                        children: [
                                            _jsx(Grid, {
                                                item: true,
                                                xs: 4,
                                                md: 3.25,
                                                display: 'flex',
                                                alignItems: 'center',
                                                children: _jsx(Typography, {
                                                    variant: 'subtitle2',
                                                    children: index === 0 ? formatDisplayDate(date) : ''
                                                })
                                            }),
                                            _jsxs(Grid, {
                                                item: true,
                                                xs: 5,
                                                md: 4.75,
                                                children: [
                                                    _jsx(CustomSelect, {
                                                        options: options,
                                                        label: options.length ? 'Registration' : 'No hour types available',
                                                        selectedOption: row.assignmentReasonId,
                                                        disabled: options.length === 0 || isSaving || requiresRefresh,
                                                        handleChange: (event) => handleReasonChange(row.localId, event.target.value),
                                                        errormsg: errors[row.localId]?.assignmentReasonId
                                                    }),
                                                    routeHint &&
                                                        _jsx(Typography, {
                                                            variant: 'caption',
                                                            color: 'text.secondary',
                                                            sx: { display: 'block', mt: 0.5 },
                                                            children: routeHint
                                                        })
                                                ]
                                            }),
                                            _jsxs(Grid, {
                                                item: true,
                                                xs: 2,
                                                md: 3,
                                                children: [
                                                    _jsx(QInput, {
                                                        placeholder: '0',
                                                        value: row.hours,
                                                        disabled: isSaving || requiresRefresh || isRetainedTechnicalPlaceholder(row),
                                                        onChange: (value) => handleHoursChange(row.localId, value)
                                                    }),
                                                    errors[row.localId]?.hours &&
                                                        _jsx(Typography, {
                                                            variant: 'caption',
                                                            fontWeight: 500,
                                                            color: '#C62828',
                                                            role: 'alert',
                                                            children: errors[row.localId].hours
                                                        })
                                                ]
                                            }),
                                            _jsx(Grid, {
                                                item: true,
                                                xs: 1,
                                                md: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                children: dateRows.length > 1 && index > 0
                                                    ? _jsx(IconButton, {
                                                        onClick: () => removeRow(row.localId),
                                                        disabled: isSaving || requiresRefresh,
                                                        'aria-label': `Remove entry for ${formatDisplayDate(date)}`,
                                                        children: _jsx(Minus, {})
                                                    })
                                                    : _jsx(IconButton, {
                                                        onClick: () => addRow(date),
                                                        disabled: isSaving ||
                                                            requiresRefresh ||
                                                            totalForDate >= 17 ||
                                                            (hourTypesByDate[date] ?? []).length === 0,
                                                        'aria-label': `Add entry for ${formatDisplayDate(date)}`,
                                                        children: _jsx(Add, {})
                                                    })
                                            })
                                        ]
                                    }, row.localId);
                                })),
                                _jsx(Grid, {
                                    item: true,
                                    xs: 12,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    children: _jsxs(Box, {
                                        width: { xs: '100%', sm: 200 },
                                        children: [
                                            _jsx(Typography, { variant: 'subtitle2', mb: 0.5, children: 'Total hours' }),
                                            _jsx(QInput, { value: totalHours, disabled: true })
                                        ]
                                    })
                                }),
                                _jsx(Grid, {
                                    item: true,
                                    xs: 12,
                                    children: _jsx(Box, {
                                        px: { xs: 0, md: 5 },
                                        children: _jsx(Button, {
                                            fullWidth: true,
                                            variant: 'contained',
                                            size: 'large',
                                            sx: { color: '#FFFFFF' },
                                            'data-action-motion': 'true',
                                            onClick: saveWeeklyHours,
                                            disabled: !dirty ||
                                                isSaving ||
                                                requiresRefresh ||
                                                rows.length === 0 ||
                                                Object.values(hourTypesByDate).some((types) => types.length === 0),
                                            children: isSaving ? 'Saving weekly hours…' : 'Save weekly hours'
                                        })
                                    })
                                })
                            ]
                        })
                ]
            })
        ]
    });
};
